// src/components/EventManagement/CloneEventModal.js
import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import InputText from '../Input/InputText';
import Typography from '../Typography';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';

const CloneEventModal = ({ isOpen, onClose, onConfirm, eventTitle, isLoading }) => {
  const { t } = useTranslation('manageEventOptions');
  const [startDay, setStartDay] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDay, setEndDay] = useState('');
  const [endTime, setEndTime] = useState('');
  const [errors, setErrors] = useState({});

  // Validar fechas
  const validateDates = () => {
    const newErrors = {};

    if (!startDay) {
      newErrors.startDay = t('cloneEvent.validation.startDateRequired');
    }

    if (!startTime) {
      newErrors.startTime = t('cloneEvent.validation.startTimeRequired');
    }

    if (!endDay) {
      newErrors.endDay = t('cloneEvent.validation.endDateRequired');
    }

    if (!endTime) {
      newErrors.endTime = t('cloneEvent.validation.endTimeRequired');
    }

    if (startDay && endDay && startTime && endTime) {
      const startDateTime = new Date(`${startDay}T${startTime}`);
      const endDateTime = new Date(`${endDay}T${endTime}`);

      if (startDateTime >= endDateTime) {
        newErrors.endDay = t('cloneEvent.validation.endDateAfterStart');
      }

      // Validar que la fecha de inicio no sea en el pasado
      const now = new Date();
      if (startDateTime <= now) {
        newErrors.startDay = t('cloneEvent.validation.pastDate');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateDates()) {
      const startDate = new Date(`${startDay}T${startTime}`);
      const endDate = new Date(`${endDay}T${endTime}`);

      onConfirm({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
    }
  };

  const handleClose = () => {
    setStartDay('');
    setStartTime('');
    setEndDay('');
    setEndTime('');
    setErrors({});
    onClose();
  };

  // Handlers para los inputs de fecha y hora
  const handleDateClick = (e) => {
    if (e.target.showPicker) e.target.showPicker();
  };

  const handleTimeClick = (e) => {
    if (e.target.showPicker) e.target.showPicker();
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={handleClose} maxWidth="500px" title={t('cloneEvent.title')}>
      <ModalContent>

        <EventInfo>
          <Typography as="p" $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultStrong}>
            {t('cloneEvent.description', { eventTitle })}
          </Typography>
        </EventInfo>

        <DateSection>
          <DateInputs>
            <Row>
              <StyledLabel as="label" $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultStrong}>
                {t('cloneEvent.startLabel')}
              </StyledLabel>
              <InputGroup>
                <DateInput
                  size="medium"
                  type="date"
                  value={startDay}
                  onChange={(e) => {
                    setStartDay(e.target.value);
                    if (errors.startDay) {
                      setErrors(prev => ({ ...prev, startDay: null }));
                    }
                  }}
                  onClick={handleDateClick}
                  onKeyDown={(e) => e.preventDefault()}
                  $variant={errors.startDay ? 'error' : ''}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
                <TimeInput
                  size="medium"
                  type="time"
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    if (errors.startTime) {
                      setErrors(prev => ({ ...prev, startTime: null }));
                    }
                  }}
                  onClick={handleTimeClick}
                  onKeyDown={(e) => e.preventDefault()}
                  $variant={errors.startTime ? 'error' : ''}
                  required
                />
              </InputGroup>
              {errors.startDay && (
                <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                  {errors.startDay}
                </Typography>
              )}
              {errors.startTime && (
                <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                  {errors.startTime}
                </Typography>
              )}
            </Row>

            <Row>
              <StyledLabel as="label" $variant="body-1-medium" $variantTablet="body-1-medium" $variantMobile="body-2-medium" color={theme.colors.defaultStrong}>
                {t('cloneEvent.endLabel')}
              </StyledLabel>
              <InputGroup>
                <DateInput
                  size="medium"
                  type="date"
                  value={endDay}
                  onChange={(e) => {
                    setEndDay(e.target.value);
                    if (errors.endDay) {
                      setErrors(prev => ({ ...prev, endDay: null }));
                    }
                  }}
                  onClick={handleDateClick}
                  onKeyDown={(e) => e.preventDefault()}
                  $variant={errors.endDay ? 'error' : ''}
                  min={startDay || new Date().toISOString().split('T')[0]}
                  required
                />
                <TimeInput
                  size="medium"
                  type="time"
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    if (errors.endTime) {
                      setErrors(prev => ({ ...prev, endTime: null }));
                    }
                  }}
                  onClick={handleTimeClick}
                  onKeyDown={(e) => e.preventDefault()}
                  $variant={errors.endTime ? 'error' : ''}
                  required
                />
              </InputGroup>
              {errors.endDay && (
                <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                  {errors.endDay}
                </Typography>
              )}
              {errors.endTime && (
                <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                  {errors.endTime}
                </Typography>
              )}
            </Row>
          </DateInputs>
        </DateSection>

        <Actions>
          <Button
            $variant="ghost"
            size="l"
            sizeTablet="l"
            sizeMobile="m"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('cloneEvent.cancel')}
          </Button>
          <Button
            $variant=""
            size="l"
            sizeTablet="l"
            sizeMobile="m"
            onClick={handleConfirm}
            disabled={isLoading || !startDay || !startTime || !endDay || !endTime}
          >
            {isLoading ? t('cloneEvent.cloning') : t('cloneEvent.confirm')}
          </Button>
        </Actions>
      </ModalContent>
    </Modal>
  );
};

export default CloneEventModal;

// Styled Components
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventInfo = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
`;

const DateSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.sm};
  padding-top: 0px;
`;

const DateInputs = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 12px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const StyledLabel = styled(Typography)`
  width: 64px;
  flex-shrink: 0;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }
`;

const DateInput = styled(InputText)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right-color: transparent;
  width: 160px;
  flex: 1;
  transition: all 0.3s ease-in-out;

  &:focus {
    border-right-color: ${theme.colors.primaryMain};
    outline: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
  
`;

const TimeInput = styled(InputText)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  width: 100px;
  flex-shrink: 0;

`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${theme.border.defaultSubtle};
`;